import { useEffect } from 'react';
import { Typography, Link, ListItem, Box, List } from '@mui/material';
/**
 * Generated from https://docs.google.com/document/d/1I3SqAPvbGl6dTKnxAPOsr3PKrDbYMW4Zr3MpzdOtIIo
 * Download as .html & adapted for React
 */
export const LpTermsOfUseText = (): JSX.Element => {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  return (
    <Box
      component={'section'}
      pb={12}
      sx={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
        fontWeight: 400,
        letterSpacing: 0,
        fontSize: '18px',
      }}
    >
      <Typography variant="h1" mb={2}>
        Terms of use
      </Typography>
      <p>
        AgileEngine websites, LaunchPod
        <br />
        Version 1.1 April 25, 2025
      </p>
      <List
        sx={{
          listStyle: 'upper-roman',
          my: 6,
        }}
      >
        <ListItem>
          <Link href="#preamble">Preamble</Link>
        </ListItem>
        <ListItem>
          <Link href="#definitions">Definitions</Link>
        </ListItem>
        <ListItem>
          <Link href="#rights">
            Rights and obligations of the website users
          </Link>
        </ListItem>
        <ListItem>
          <Link href="#communication">Communication with AgileEngine</Link>
        </ListItem>
        <ListItem>
          <Link href="#messages">
            Messages and newsletters from AgileEngine
          </Link>
        </ListItem>
        <ListItem>
          <Link href="#rights">Intellectual property rights</Link>
        </ListItem>
        <ListItem>
          <Link href="#data">Personal data</Link>
        </ListItem>
        <ListItem>
          <Link href="#cookies">Cookies policy</Link>
        </ListItem>
        <ListItem>
          <Link href="#responsibility">Responsibility</Link>
        </ListItem>
        <ListItem>
          <Link href="#misc">Miscellaneous</Link>
        </ListItem>
      </List>
      <Typography variant="h2" id="preamble">
        I. Preamble
      </Typography>
      <p>
        These Terms of Use (“ToU”) set out the rules for accessing and using the
        websites operated by AgileEngine companies, as well as the principles
        governing the processing of your personal data.
      </p>
      <p>
        Please note that these ToU do not apply to the relationship between
        AgileEngine and Users in relation to applying for positions or receiving
        services from AgileEngine. Information on those matters is available
        separately on the AgileEngine websites.
      </p>
      <p>
        These ToU are binding for both AgileEngine and the Users. AgileEngine
        reserves the right to amend the ToU, in whole or in part, at its sole
        discretion. Any updated version of the ToU becomes effective upon
        publication on the website.
      </p>
      <p>
        By accessing or using the AgileEngine websites, you agree to be bound by
        the version of the ToU in effect at the time of access. If you do not
        agree with the ToU, please discontinue your use of the websites
        immediately.
      </p>
      <p>
        AgileEngine is the sole rightsholder of all intellectual property rights
        related to the websites and their content.
      </p>
      <p>
        Please note that the websites do not support user-generated comments. To
        contact AgileEngine, you may use the communication options provided on
        the websites.
      </p>
      <p>
        Access to AgileEngine websites and services is strictly limited to users
        who are 16 years of age or older.
      </p>
      <Typography variant="h2" id="definitions" mt={6}>
        II. Definitions
      </Typography>
      <p>
        <b>AgileEngine</b> means all companies related to AgileEngine LLC.
      </p>
      <p>
        <b>Content</b> means all static and dynamic elements, information,
        texts, images, audio and video files, databases, trademarks and logos,
        technical and graphic solutions inserted to the Websites.
      </p>
      <p>
        <b>Cookies</b> mean text files sent from the Websites to the Users’
        electronic devices and saved with web-browsers. Such files do not
        identify the Users, but fix information on their actions related to use
        of the Websites. Such information is collected for improvement of
        operation of the Websites and customization of the respective settings:
        monitoring preferences of the Users, “auto-fill” option, sharing
        information in social media, customization of Services to the Users’
        preferences, collection of general statistic information on use of the
        Websites.
      </p>
      <p>
        <b>Personal</b> data means the personal data collected via the Websites,
        i.e. the information that directly or indirectly identifies the Users.
      </p>
      <p>
        <b>Positions</b> means all opportunities with vacancies and different
        types of the engagement according to the requirements for the project.
      </p>
      <p>
        <b>Service(s)</b> means any service and related functions which are
        provided by AgileEngine in favor of the Users complimentary/for some
        fees available for order on the Websites (if applicable).
      </p>
      <p>
        <b>Terms of Use</b> means the rules governing use of the Websites.
      </p>
      <p>
        <b>User</b> means any natural person or legal entity gaining access,
        browsing or visiting the Websites with or without order of the Services.
      </p>
      <p>
        <b>Use of AgileEngine Websites</b> means any web-browsing, downloading
        or any other use of the Content of AgileEngine Websites, use of the
        Services provided by AgileEngine.
      </p>
      <p>
        <b>Websites</b> mean the websites published by AgileEngine (inclusively{' '}
        <Link href="https://launchpod.agileengine.com" target="_blank">
          https://launchpod.agileengine.com
        </Link>
        ).
      </p>
      <Typography variant="h2" id="rights" mt={6}>
        III. Rights and obligations of the users of the websites
      </Typography>
      <p>The Websites may only be used for the following purposes:</p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          To browse the available Content regarding AgileEngine’s Services and
          open Positions;
        </ListItem>
        <ListItem>To order Services offered by AgileEngine;</ListItem>
        <ListItem>
          To manage specific aspects of the Services provided by AgileEngine;
        </ListItem>
        <ListItem>
          To submit application information as a candidate for a Position;
        </ListItem>
        <ListItem>
          To communicate with AgileEngine by sending messages, comments,
          complaints, or suggestions via the feedback form available on the
          Websites, and to receive responses from AgileEngine;
        </ListItem>
        <ListItem>
          For other purposes explicitly provided via the AgileEngine Websites.
        </ListItem>
      </List>
      <p>
        Any Use of AgileEngine Website for purposes other than those listed
        above is prohibited.
      </p>
      <p>Users are required to:</p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          Comply with these Terms of Use and all applicable laws and
          regulations;
        </ListItem>
        <ListItem>
          Use the Websites solely for the permitted purposes listed above;
        </ListItem>
        <ListItem>
          Refrain from using any content from the Websites for commercial or
          non-commercial purposes without prior written permission from
          AgileEngine; this includes, but is not limited to, decompiling,
          reverse engineering, disassembling, or attempting to access or modify
          the source code of the Websites;
        </ListItem>
        <ListItem>
          Not use the Websites to send messages that are violent, abusive,
          defamatory, or that infringe the rights, dignity, or business
          reputation of AgileEngine or any third parties, including sending
          unsolicited commercial messages ("spam");
        </ListItem>
        <ListItem>
          Not use software or other means to interfere with or attempt to
          disrupt the operation of the Websites.
        </ListItem>
      </List>
      <Typography variant="h2" id="communication" mt={6}>
        IV. Communication with AgileEngine
      </Typography>
      <p>Users may contact AgileEngine through the following channels:</p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          By submitting a message (e.g., request or complaint) via the feedback
          form available on the Websites;
        </ListItem>
        <ListItem>
          By sending an email to the relevant addresses listed on the Websites.
        </ListItem>
      </List>
      <p>
        To facilitate communication and receive a response from AgileEngine,
        Users are required to provide the following Personal Data: full name,
        email address, telephone number, and city and state of residence. For
        more information on how Personal Data is collected, processed, and
        shared with third parties, please refer to the relevant section of these
        Terms of Use and the Privacy Policy available at:{' '}
        <Link href="https://agileengine.com/privacy-policy/">
          https://agileengine.com/privacy-policy/
        </Link>
      </p>
      <p>
        <b>Prohibited Content</b>
      </p>
      <p>
        Users are prohibited from submitting the following types of Content via
        the Websites:
      </p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          Messages that are violent, abusive, disrespectful, or otherwise
          infringe upon the rights, honor, dignity, or business reputation of
          AgileEngine or any third party;
        </ListItem>
        <ListItem>Unsolicited commercial messages (“spam”).</ListItem>
      </List>
      <p>
        AgileEngine reserves the right not to respond to such prohibited
        messages and may restrict the user’s ability to use the feedback form
        for future communication.
      </p>
      <p>
        <b>Communication Monitoring</b>
      </p>
      <p>
        Communication between Users and AgileEngine may be recorded using
        technical tools. These records are stored by AgileEngine for the purpose
        of Service quality monitoring and operational improvement.
      </p>
      <Typography variant="h2" id="messages" mt={6}>
        V. Messages and newsletters from AgileEngine
      </Typography>
      <p>
        <b>Messages from AgileEngine</b>
      </p>
      <p>Users may receive the following types of messages from AgileEngine:</p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          Responses to requests or complaints submitted via the feedback forms
          on the Websites;
        </ListItem>
        <ListItem>
          Messages related to the processing and management of Position
          applications or Service requests submitted through the Websites,
          including follow-up communications regarding AgileEngine's Services.
        </ListItem>
      </List>
      <p>
        AgileEngine may contact Users via SMS, email, messenger apps, feedback
        form messages, or social media platforms—depending on the contact
        details provided by the User.
      </p>
      <p>
        Users can manage their communication preferences through the settings
        available on the AgileEngine Websites (subject to available
        functionality).
      </p>
      <p>
        <b>Newsletters</b>
      </p>
      <p>
        AgileEngine may also send newsletters containing updates about Positions
        and Services offered by AgileEngine.
      </p>
      <p>
        Newsletters are only sent to contacts included in AgileEngine’s User
        database based on prior consent. This consent may be given in the
        following ways:
      </p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>By subscribing to newsletters on the Websites;</ListItem>
        <ListItem>
          By submitting an application for a Position via the Websites.
        </ListItem>
      </List>
      <p>
        Newsletters may be sent via SMS, email, messenger apps, social media (if
        contact details were provided), or through the User account chat.
      </p>
      <p>
        The content of newsletters is customized by AgileEngine based on User
        preferences and profile data.
      </p>
      <p>
        Users may unsubscribe from newsletters at any time by clicking the
        unsubscribe link provided in each newsletter message.
      </p>
      <Typography variant="h2" id="rights" mt={6}>
        VI. Intellectual property rights
      </Typography>
      <p>
        AgileEngine is the exclusive holder of all intellectual property rights
        and related rights to the Content available on the Websites, unless such
        rights have been lawfully licensed to AgileEngine by third parties.
      </p>
      <p>
        Any use of the Content—whether for commercial or non-commercial
        purposes—in whole or in part, is strictly prohibited without prior
        written authorization from AgileEngine. This includes, but is not
        limited to: reproduction, display, adaptation, modification,
        publication, transmission, distortion, or integration into other
        Websites or platforms.
      </p>
      <p>
        If you wish to use any part of the Content, you must submit a request to
        AgileEngine using the feedback form available on the Websites.
      </p>
      <p>
        In the event that AgileEngine's intellectual property rights are
        infringed by any third party, AgileEngine reserves the right to pursue
        all available legal remedies, including initiating court proceedings.
      </p>
      <Typography variant="h2" id="data" mt={6}>
        VII. Personal data
      </Typography>
      <p>
        AgileEngine collects and processes Users’ Personal Data to provide
        access to its Websites, Positions, and Services. Personal Data is
        processed in accordance with applicable laws and may include contact
        details, login credentials, cookies, and other technical data.
        AgileEngine does not process sensitive data or data of individuals under
        16 years of age, nor does it sell or share Personal Data for monetary
        gain.
      </p>
      <p>
        Personal data is collected through AgileEngine’s websites for two
        primary purposes: facilitating the use and ordering of positions or
        services, and supporting informational and marketing activities. The
        specific data collected, the purposes for which it is processed, and the
        applicable legal bases may vary depending on the context.
      </p>
      <p>
        When users engage with websites that enable them to order AgileEngine’s
        positions or services, the company processes personal data such as name,
        surname, email address, mobile number, and city and state of residence.
        This information is used to create user accounts, provide access to
        services, manage service orders, submit applications, track preferences
        for personalized content, and respond to feedback through forms. In
        addition, cookies and technical data are used to tailor user
        experiences, respond to information and search requests, and improve
        website functionality.{' '}
      </p>
      <p>
        On websites focused on informational and marketing purposes, AgileEngine
        collects similar categories of data - name, surname, email address,
        mobile number, and city and country of residence. This data supports the
        use of feedback forms and the distribution of content related to
        positions and services, including newsletters, promotional updates, and
        event notifications. These activities are also based on user consent.
        Cookies and technical data from marketing and informational websites
        help enable communication through email and social media, customize
        content based on user behavior, and respond to queries related to
        AgileEngine’s offerings. As with other processing activities, these rely
        on the user's consent.
      </p>
      <p>
        Depending on the nature of the processing, different AgileEngine
        entities may act as data controllers or processors. Data may be
        transferred to affiliated companies, service providers, or public
        authorities as necessary, with appropriate safeguards in place to
        protect users’ information.
      </p>
      <p>
        Users have rights regarding access, correction, restriction, and
        deletion of their data. Requests may be sent to{' '}
        <Link href="mailto:policy@agileengine.com">policy@agileengine.com</Link>
        .
      </p>
      <p>
        There is a log-in option for the Websites via social networks managed by
        third parties (depending on the functionality and internal settings of
        the Websites). By making such an entry, the Users hereby agree to
        provide AgileEngine with the shared access to Personal data to the scope
        requested under the settings on Personal data in the respective social
        network.
      </p>
      <p>
        For full details, please see our{' '}
        <Link href="https://agileengine.com/privacy-policy/" target="_blank">
          Privacy Policy
        </Link>
        .
      </p>
      <Typography variant="h2" id="cookies" mt={6}>
        VIII. Cookies policy
      </Typography>
      <p>
        Use of the Websites requires the use of "Cookies" installed on the
        Users' electronic devices.
      </p>
      <p>AgileEngine uses the following categories of "Cookies":</p>

      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          to provide Users with consistent access to functionality of the
          Websites;
        </ListItem>
        <ListItem>to improve and facilitate Use of the Websites;</ListItem>
        <ListItem>
          to correctly display advertising messages of AgileEngine on the
          Websites, third-party websites, and social networks;
        </ListItem>
        <ListItem>
          to provide Users with access to exchange information from the Websites
          via social networks;
        </ListItem>
        <ListItem>
          to monitor audience metrics of the Websites (traffic analysis, usage
          trends, etc.).
        </ListItem>
      </List>
      <p>
        Prior to using "Cookies" on the Websites, AgileEngine requests the
        Users’ consent to such use by sending the respective notification (in
        banner form).
      </p>
      <p>
        There are the following options provided for the Users in respect of
        "Cookies" settings:
      </p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>allow automatic storage and use of "Cookies";</ListItem>
        <ListItem>warn about "Cookies" use;</ListItem>
        <ListItem>customize "Cookies" use;</ListItem>
        <ListItem>
          block "Cookies" use (in such a case, there may be some restrictions on
          the operation of the Websites, e.g., no option for customized
          settings, etc.).
        </ListItem>
      </List>
      <p>
        When using the Websites, it is possible to use functional options from
        other resources, in particular:
      </p>
      <List sx={{ mb: 3, mt: -1.5, maxWidth: '40ch' }}>
        <ListItem>
          social networks (there is an option of direct Content sharing from the
          Websites) (depending on functionality and internal settings of the
          Websites). These platforms have separate "Cookies" policies;
        </ListItem>
        <ListItem>
          Google Analytics, for collecting general statistical information on
          the Use of the Websites (such information is used by AgileEngine
          exclusively for improving the Content and provision of the Services
          ordered via the Websites). The operation of Google Analytics is
          governed by the respective policy of Google;
        </ListItem>
        <ListItem>
          other resources, which have been verified for safety by AgileEngine.
        </ListItem>
      </List>
      <p>
        You may also see advertisements of our Websites on third-party websites,
        such as social media. These ads may be tailored to you by Cookies (which
        track your web activity and allow us to show ads to Users who have
        visited our Website). You can see ads on social media because we engage
        a social network to show ads to our customers or Users that match the
        demographic profile of our customers or Users. In some cases, this may
        involve the transfer of your email address, social network profile, and
        phone number. If you no longer wish to see personalized ads, you may
        change your Cookies and privacy settings in your browser and on
        third-party websites.
      </p>
      <p>
        You can see our ads on AgileEngine Websites and third-party applications
        and websites, as well as across various devices you use. We may use this
        process to improve the effectiveness of our marketing efforts for the
        User.
      </p>
      <p>
        Please note that if the "Cookies" function is blocked, it may result in
        improper operation of the Websites. There is an option to block
        "Cookies" either partially or fully (for certain types of "Cookies") by
        modifying the settings of your web browser on the devices used for
        accessing the Websites.
      </p>
      <Typography variant="h2" id="responsibility" mt={6}>
        IX. Responsibility
      </Typography>
      <p>
        Breach of any ToU provisions will lead to bringing the Users to
        responsibility established under the ToU and the law applicable to
        regulation of such ToU.
      </p>
      <p>
        There are hyperlinks to third-party web resources inserted to the
        Websites. AgileEngine is not responsible for the content posted on such
        web resources, as well as for the collection and processing of Personal
        Data of Users, which may occur as a result of the use of such resources.
      </p>
      <p>
        AgileEngine takes every effort to provide the Users with secure,
        uninterrupted and correct access to the Websites. At the same time,
        AgileEngine is not responsible for any interruptions in the work of the
        Websites, the incompatibility of such Websites with all types of the
        Users’ devices, and the inability of the Users to access the Websites
        due to the absence of an Internet connection. AgileEngine retains
        discretion to restrict the Users’ access to the Websites in full or in
        part.
      </p>
      <p>
        AgileEngine is not liable for any damages arising for the Users from Use
        of the Websites. Also, AgileEngine is not liable before any third
        parties for any Users’ actions committed with Use of the Websites.{' '}
      </p>
      <Typography variant="h2" id="misc" mt={6}>
        X. Miscellaneous
      </Typography>
      <p>
        ToU is effective as of the moment of its publishing on the Websites.
      </p>
      <p>
        Any disputes arising between AgileEngine and the Users related to Use of
        the Websites are settled with negotiations held between the parties
        (such settlement should be initiated by the Users in question with
        communicating AgileEngine with the respective request) as well as in
        accordance with the applicable law.
      </p>
      <p>
        For any questions on Use of the Websites please contact AgileEngine
        support at the feedback form on the Websites. For the issues related to
        collection and processing of Personal Data please contact AgileEngine
        data protection personnel at{' '}
        <Link href="mailto:policy@agileengine.com">policy@agileengine.com</Link>
        .
      </p>
    </Box>
  );
};
